import Swal from 'sweetalert2';

/**
 * AI Loader Configuration
 * Default loading messages for AI operations
 */
export const DEFAULT_AI_LOADING_MESSAGES = [
  { icon: '🔍', text: 'Analizzando la foto...' },
  { icon: '🤖', text: 'Elaborando con intelligenza artificiale...' },
  { icon: '🎨', text: 'Applicando le modifiche richieste...' },
  { icon: '✨', text: 'Ottimizzando i risultati...' },
  { icon: '🎯', text: 'Perfezionando i dettagli...' },
  { icon: '💎', text: 'Controllo qualità finale...' },
];

/**
 * Sleep utility function
 * @param {number} ms - Milliseconds to sleep
 * @returns {Promise} Promise that resolves after the specified time
 */
export const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Start the AI loader with progressive loading animation
 * @param {Array} loadingMessages - Array of loading messages with icon and text
 * @param {string} title - Title for the loader
 * @returns {Object} Swal instance with updateMessage and messageInterval properties
 */
export const startAiLoader = (
  loadingMessages = DEFAULT_AI_LOADING_MESSAGES,
  title = '✨ Elaborazione in corso'
) => {
  let currentMessageIndex = 0;
  let messageInterval;

  const swalInstance = Swal.fire({
    title,
    html: `
      <div class="ai-loader-container">
        <div class="loader-visual">
          <div class="loader-circle">
            <div class="loader-progress"></div>
            <div class="loader-icon">${loadingMessages[0].icon}</div>
          </div>
        </div>
        
        <div class="status-message">
          <span class="status-icon">${loadingMessages[0].icon}</span>
          <span class="status-text">${loadingMessages[0].text}</span>
        </div>
        
        <div class="progress-bar-container">
          <div class="progress-bar-fill"></div>
        </div>
        
        <div class="warning-text">
          Non chiudere questa pagina durante l'elaborazione 🙏
        </div>
      </div>
    `,
    allowOutsideClick: false,
    allowEscapeKey: false,
    allowEnterKey: false,
    showConfirmButton: false,
    showCancelButton: false,
    customClass: {
      popup: 'animated fadeIn',
    },
    didOpen: () => {
      const container = Swal.getHtmlContainer();
      if (!container) return;

      const statusIcon = container.querySelector('.status-icon');
      const statusText = container.querySelector('.status-text');
      const loaderIcon = container.querySelector('.loader-icon');

      if (!statusIcon || !statusText || !loaderIcon) {
        console.error('Could not find loader elements');
        return;
      }

      const updateMessage = (message) => {
        // Reset animations
        statusIcon.style.animation = '';
        statusText.style.animation = '';

        // Force reflow to restart animation
        void statusIcon.offsetWidth;
        void statusText.offsetWidth;

        // Update content and restart animations
        statusIcon.textContent = message.icon;
        statusText.textContent = message.text;
        statusIcon.style.animation = 'iconPop 0.3s ease-out';
        statusText.style.animation = 'fadeIn 0.3s ease-out';

        // Update central icon in sync with status icon
        loaderIcon.textContent = message.icon;
        loaderIcon.style.animation = '';
        void loaderIcon.offsetWidth; // Force reflow
        loaderIcon.style.animation = 'loaderPulse 2s ease-in-out infinite';
      };

      // Calculate interval based on number of messages
      const intervalDuration = 16800 / loadingMessages.length; // 16.8s total

      messageInterval = setInterval(() => {
        currentMessageIndex++;
        if (currentMessageIndex < loadingMessages.length) {
          updateMessage(loadingMessages[currentMessageIndex]);
        }
      }, intervalDuration);

      // Store the update function and interval reference on the swal instance
      swalInstance.updateMessage = updateMessage;
      swalInstance.messageInterval = messageInterval;
    },
    willClose: () => {
      if (messageInterval) {
        clearInterval(messageInterval);
      }
    },
  });

  return swalInstance;
};

/**
 * Stop the AI loader
 */
export const stopAiLoader = () => {
  Swal.hideLoading();
  Swal.close();
};

/**
 * Update loader to show saving step
 * @param {Object} loaderInstance - The loader instance from startAiLoader
 */
export const updateAiLoaderForSaving = (loaderInstance) => {
  // Stop automatic message rotation and show saving step
  if (loaderInstance.messageInterval) {
    clearInterval(loaderInstance.messageInterval);
  }
  if (loaderInstance.updateMessage) {
    loaderInstance.updateMessage({
      icon: '💾',
      text: 'Salvando la foto tra gli allegati...',
    });

    // Also update the progress bar to show near completion
    const progressBar = document.querySelector('.progress-bar-fill');
    if (progressBar) {
      progressBar.style.animation = 'none';
      progressBar.style.width = '90%';
      progressBar.style.transition = 'width 0.5s ease-out';
    }
  }
};

/**
 * Show AI success message with enhanced UI
 * @param {string} title - Success message title
 * @param {string} message - Success message text
 */
export const showAiSuccessMessage = (
  title = 'Modifica completata',
  message = 'La foto è stata modificata con successo!'
) => {
  Swal.fire({
    title: `✨ ${title}!`,
    html: `
      <div class="success-container">
        <div class="success-icon-wrapper">
          <div class="sparkle-container">
            <div class="success-sparkle"></div>
            <div class="success-sparkle"></div>
            <div class="success-sparkle"></div>
            <div class="success-sparkle"></div>
            <div class="success-sparkle"></div>
            <div class="success-sparkle"></div>
          </div>
          <div class="success-circle">
            <div class="success-icon">✅</div>
          </div>
        </div>
        <div class="success-message">${message}</div>
      </div>
    `,
    timer: 1000,
    showConfirmButton: false,
    customClass: {
      popup: 'animated fadeIn',
    },
  });
};

/**
 * AI Feature Loading Messages
 * Predefined loading messages for various AI features
 */
export const AI_FEATURE_LOADING_MESSAGES = {
  SMILE: [
    { icon: '😊', text: 'Analizzando il viso...' },
    { icon: '🔍', text: 'Identificando i punti del sorriso...' },
    { icon: '✨', text: 'Applicando il sorriso naturale...' },
    { icon: '🎨', text: 'Adattando alle espressioni facciali...' },
    { icon: '💫', text: 'Perfezionando i dettagli...' },
    { icon: '💎', text: 'Controllo qualità finale...' },
  ],
  AUTOCOLOR: [
    { icon: '🎨', text: 'Analizzando i colori esistenti...' },
    { icon: '🌈', text: 'Migliorando la saturazione...' },
    { icon: '☀️', text: 'Ottimizzando la luminosità...' },
    { icon: '🎯', text: 'Bilanciando i toni...' },
    { icon: '✨', text: 'Applicando correzioni cromatiche...' },
    { icon: '💎', text: 'Controllo qualità finale...' },
  ],
  COLORIZE_BW: [
    { icon: '🖼️', text: 'Analizzando la foto in bianco e nero...' },
    { icon: '🔍', text: 'Identificando gli elementi della scena...' },
    { icon: '🎨', text: 'Generando i colori appropriati...' },
    { icon: '🌈', text: 'Applicando la colorazione...' },
    { icon: '✨', text: 'Bilanciando i toni cromatici...' },
    { icon: '💎', text: 'Controllo qualità finale...' },
  ],
  EXPAND: [
    { icon: '🖼️', text: 'Analizzando la scena attuale...' },
    { icon: '🔍', text: 'Identificando i bordi da espandere...' },
    { icon: '🎨', text: 'Generando nuovi contenuti...' },
    { icon: '🌟', text: 'Espandendo la scena...' },
    { icon: '✨', text: 'Integrando con la foto originale...' },
    { icon: '💎', text: 'Controllo qualità finale...' },
  ],
  MAGIC_ERASER: [
    { icon: '🔍', text: 'Analizzando gli elementi da rimuovere...' },
    { icon: '✂️', text: 'Identificando le aree selezionate...' },
    { icon: '🧹', text: 'Rimuovendo gli oggetti indesiderati...' },
    { icon: '🎨', text: 'Ricostruendo lo sfondo...' },
    { icon: '✨', text: 'Perfezionando i dettagli...' },
    { icon: '💎', text: 'Controllo qualità finale...' },
  ],
  ENHANCE_RESOLUTION: [
    { icon: '🔍', text: 'Analizzando la risoluzione attuale...' },
    { icon: '🔬', text: 'Esaminando i dettagli della foto...' },
    { icon: '⚙️', text: 'Applicando algoritmi di upscaling...' },
    { icon: '✨', text: 'Migliorando la nitidezza...' },
    { icon: '🎯', text: 'Ottimizzando i dettagli fini...' },
    { icon: '💎', text: 'Controllo qualità finale...' },
  ],
  ENHANCE_FACE: [
    { icon: '👤', text: 'Identificando i volti nella foto...' },
    { icon: '🔍', text: 'Analizzando le caratteristiche facciali...' },
    { icon: '✨', text: 'Migliorando la qualità del viso...' },
    { icon: '🎨', text: 'Perfezionando i dettagli della pelle...' },
    { icon: '💫', text: 'Ottimizzando luci e ombre...' },
    { icon: '💎', text: 'Controllo qualità finale...' },
  ],
  MAKE_YOUNGER: [
    { icon: '👤', text: 'Analizzando il viso...' },
    { icon: '🔍', text: 'Identificando i segni del tempo...' },
    { icon: '✨', text: 'Riducendo le rughe...' },
    { icon: '🌟', text: 'Migliorando la luminosità della pelle...' },
    { icon: '💫', text: 'Perfezionando i contorni...' },
    { icon: '💎', text: 'Controllo qualità finale...' },
  ],
  REMOVE_BG: [
    { icon: '📸', text: 'Analizzando la foto...' },
    { icon: '👤', text: 'Identificando il soggetto principale...' },
    { icon: '✂️', text: 'Separando il soggetto dallo sfondo...' },
    { icon: '🎨', text: 'Rifinendo i contorni...' },
    { icon: '✨', text: 'Ottimizzando la trasparenza...' },
    { icon: '💎', text: 'Controllo qualità finale...' },
  ],
  BLUR_BG: [
    { icon: '📸', text: 'Analizzando la foto...' },
    { icon: '👤', text: 'Identificando il soggetto principale...' },
    { icon: '🎯', text: 'Isolando il primo piano...' },
    { icon: '🌫️', text: 'Applicando la sfocatura allo sfondo...' },
    { icon: '✨', text: 'Bilanciando profondità e nitidezza...' },
    { icon: '💎', text: 'Controllo qualità finale...' },
  ],
};
